/**
 * Fetch wrapper that attaches an Entra Bearer token to every request.
 *
 * Failures surface as `ApiError` with the HTTP status + correlation ID
 * pulled from the response. Callers can choose to display the
 * correlation ID for support tickets without exposing stack traces.
 */
import { acquireAccessToken } from "@/lib/auth";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api";

export class ApiError extends Error {
  readonly status: number;
  readonly correlationId: string | null;

  constructor(message: string, status: number, correlationId: string | null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.correlationId = correlationId;
  }
}

export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const token = await acquireAccessToken();
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  if (!headers.has("Content-Type") && init.body) {
    headers.set("Content-Type", "application/json");
  }

  const url = path.startsWith("http") ? path : `${API_BASE_URL}${path}`;
  const response = await fetch(url, { ...init, headers });
  const correlationId = response.headers.get("X-Correlation-ID");

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    let message = text || response.statusText;
    try {
      const parsed = JSON.parse(text) as { detail?: unknown; error?: unknown };
      if (typeof parsed.detail === "string") message = parsed.detail;
      else if (typeof parsed.error === "string") message = parsed.error;
    } catch {
      // not JSON, that's fine
    }
    throw new ApiError(message, response.status, correlationId);
  }

  if (response.status === 204 || response.headers.get("content-length") === "0") {
    return undefined as T;
  }

  return (await response.json()) as T;
}
